/**
 * =============================================================================
 * Página: /afiliado/perfil
 * Versão: 2.0.0 (Redesign Premium Mobile-First)
 *
 * Descrição:
 *   Edição de dados pessoais e PIX em layout premium com seções claras,
 *   ícones em cápsula e CTA principal com gradiente.
 * =============================================================================
 */
import { useEffect, useState } from 'react';
import { Loader2, UserCircle, KeyRound, User, MessageSquareQuote } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { AffiliateAvatarUploader } from '@/components/affiliate/AffiliateAvatarUploader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  useAffiliateProfile,
  useUpdateAffiliateProfile,
  type AffiliateProfileUpdate,
} from '@/hooks/useAffiliateProfile';
import { AffiliatePageHeader } from '@/components/affiliate/shared/AffiliatePageHeader';
// Componente reutilizável de telefone com seletor de DDI + máscara BR inteligente
import { PhoneInput } from '@/components/ui/phone-input';
// Helpers de máscara para CPF/CNPJ (já testados em outras páginas do sistema)
import { applyCPFMask, applyCNPJMask } from '@/lib/input-masks';

const PIX_KEY_TYPES = [
  { value: 'cpf', label: 'CPF' },
  { value: 'cnpj', label: 'CNPJ' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone' },
  { value: 'random', label: 'Chave aleatória' },
];

const DOC_TYPES = [
  { value: 'cpf', label: 'CPF' },
  { value: 'cnpj', label: 'CNPJ' },
];

export default function AffiliateProfile() {
  const { data: profile, isLoading, isError, refetch } = useAffiliateProfile();
  const updateProfile = useUpdateAffiliateProfile();

  const [form, setForm] = useState<AffiliateProfileUpdate>({});

  useEffect(() => {
    if (profile) {
      setForm({
        full_name: profile.full_name ?? '',
        phone: profile.phone ?? '',
        document_number: profile.document_number ?? '',
        document_type: profile.document_type ?? '',
        pix_key: profile.pix_key ?? '',
        pix_key_type: profile.pix_key_type ?? '',
        bio_comment: profile.bio_comment ?? '',
      });
    }
  }, [profile]);

  function setField<K extends keyof AffiliateProfileUpdate>(
    key: K,
    value: AffiliateProfileUpdate[K],
  ) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.full_name || !form.full_name.trim()) {
      toast.error('Informe seu nome completo.');
      return;
    }
    // Comentário: trim → string vazia vira null para sinalizar limpeza no servidor.
    const trimmedBio = (form.bio_comment ?? '').trim();
    const payload: AffiliateProfileUpdate = {
      full_name: form.full_name.trim(),
      phone: form.phone?.trim() || null,
      document_number: form.document_number?.trim() || null,
      document_type: form.document_type || null,
      pix_key: form.pix_key?.trim() || null,
      pix_key_type: form.pix_key_type || null,
      bio_comment: trimmedBio.length > 0 ? trimmedBio : null,
    };

    try {
      await updateProfile.mutateAsync(payload);
      toast.success('Perfil atualizado com sucesso.');
    } catch (err: any) {
      toast.error(err?.message ?? 'Erro ao atualizar perfil.');
    }
  }

  return (
    <div className="container mx-auto max-w-3xl p-4 md:p-6 space-y-6 aff-animate-in pb-32 md:pb-6">
      <AffiliatePageHeader
        icon={<User className="h-5 w-5" />}
        title="Perfil"
        subtitle="Atualize seus dados pessoais e a chave PIX para recebimento."
      />

      {isLoading && (
        <div className="aff-card p-6 space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-2/3" />
        </div>
      )}

      {isError && !isLoading && (
        <div className="aff-card flex flex-col items-center gap-3 px-6 py-10 text-center">
          <p className="text-sm text-[hsl(var(--aff-muted-foreground))]">
            Não foi possível carregar seu perfil.
          </p>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            Tentar novamente
          </Button>
        </div>
      )}

      {!isLoading && !isError && profile && (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Seção: Foto de perfil (upload/remoção) */}
          <AffiliateAvatarUploader
            affiliateId={profile.id}
            affiliateName={profile.full_name}
            currentAvatarUrl={profile.avatar_url}
          />

          {/* Seção: Dados pessoais */}
          <section className="aff-card overflow-hidden">
            <header className="flex items-center gap-3 border-b border-[hsl(var(--aff-border))] px-5 py-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[hsl(var(--aff-primary-soft))] text-[hsl(var(--aff-primary))]">
                <UserCircle className="h-4.5 w-4.5" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-[hsl(var(--aff-foreground))]">
                  Dados pessoais
                </h2>
                <p className="text-[11px] text-[hsl(var(--aff-muted-foreground))]">
                  Suas informações de identificação
                </p>
              </div>
            </header>
            <div className="p-5 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="full_name">Nome completo *</Label>
                <Input
                  id="full_name"
                  value={form.full_name ?? ''}
                  onChange={(e) => setField('full_name', e.target.value)}
                  required
                />
              </div>

              {/*
                E-mail e Telefone na mesma linha (no desktop). Telefone usa
                o componente <PhoneInput> compartilhado, que já entrega:
                  - Seletor de DDI (lista de países)
                  - Máscara BR inteligente (celular, fixo, 0800, 4003...)
                  - Autocorreção de dígitos extras vindos de paste/autofill
              */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="email">E-mail</Label>
                  <Input id="email" value={profile.email} disabled readOnly />
                  <p className="text-[11px] text-[hsl(var(--aff-muted-foreground))]">
                    Para alterar o e-mail, entre em contato com o suporte.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="phone">Telefone</Label>
                  <PhoneInput
                    id="phone"
                    value={form.phone ?? ''}
                    onChange={(v) => setField('phone', v)}
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>

              {/*
                Tipo de documento + Número do documento na mesma linha.
                A máscara do número é aplicada dinamicamente conforme o tipo
                selecionado:
                  - CPF: 000.000.000-00
                  - CNPJ: 00.000.000/0000-00
                Ao trocar o tipo, re-mascaramos o valor existente para evitar
                inconsistência visual.
              */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="document_type">Tipo de documento</Label>
                  <Select
                    value={form.document_type ?? ''}
                    onValueChange={(v) => {
                      setField('document_type', v);
                      // Re-aplica a máscara correspondente sobre os dígitos crus existentes
                      const rawDigits = (form.document_number ?? '').replace(/\D/g, '');
                      if (v === 'cpf') {
                        setField('document_number', applyCPFMask(rawDigits));
                      } else if (v === 'cnpj') {
                        setField('document_number', applyCNPJMask(rawDigits));
                      }
                    }}
                  >
                    <SelectTrigger id="document_type">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {DOC_TYPES.map((d) => (
                        <SelectItem key={d.value} value={d.value}>
                          {d.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="document_number">Número do documento</Label>
                  <Input
                    id="document_number"
                    inputMode="numeric"
                    placeholder={
                      form.document_type === 'cnpj'
                        ? '00.000.000/0000-00'
                        : form.document_type === 'cpf'
                        ? '000.000.000-00'
                        : 'Selecione o tipo primeiro'
                    }
                    disabled={!form.document_type}
                    value={form.document_number ?? ''}
                    onChange={(e) => {
                      // Aplica máscara conforme o tipo selecionado
                      const raw = e.target.value;
                      if (form.document_type === 'cpf') {
                        setField('document_number', applyCPFMask(raw));
                      } else if (form.document_type === 'cnpj') {
                        setField('document_number', applyCNPJMask(raw));
                      } else {
                        setField('document_number', raw);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Seção: Comentário público (aparece no formulário de indicação) */}
          <section className="aff-card overflow-hidden">
            <header className="flex items-center gap-3 border-b border-[hsl(var(--aff-border))] px-5 py-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[hsl(var(--aff-primary-soft))] text-[hsl(var(--aff-primary))]">
                <MessageSquareQuote className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-[hsl(var(--aff-foreground))]">
                  Comentário público
                </h2>
                <p className="text-[11px] text-[hsl(var(--aff-muted-foreground))]">
                  Mensagem curta que aparece junto com sua foto no formulário de indicação
                </p>
              </div>
            </header>
            <div className="p-5 space-y-2">
              <Label htmlFor="bio_comment">Sua mensagem (opcional)</Label>
              <Textarea
                id="bio_comment"
                value={form.bio_comment ?? ''}
                onChange={(e) => setField('bio_comment', e.target.value.slice(0, 500))}
                placeholder="Ex.: Sou cliente há 3 anos e recomendo de olhos fechados!"
                rows={3}
                maxLength={500}
              />
              <p className="text-right text-[11px] text-[hsl(var(--aff-muted-foreground))]">
                {(form.bio_comment ?? '').length}/500
              </p>
            </div>
          </section>

          {/* Seção: PIX */}
          <section className="aff-card overflow-hidden">
            <header className="flex items-center gap-3 border-b border-[hsl(var(--aff-border))] px-5 py-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[hsl(var(--aff-primary-soft))] text-[hsl(var(--aff-primary))]">
                <KeyRound className="h-4.5 w-4.5" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-[hsl(var(--aff-foreground))]">
                  Recebimento (PIX)
                </h2>
                <p className="text-[11px] text-[hsl(var(--aff-muted-foreground))]">
                  Usado quando você solicitar um saque
                </p>
              </div>
            </header>
            <div className="p-5 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="pix_key_type">Tipo da chave</Label>
                  <Select
                    value={form.pix_key_type ?? ''}
                    onValueChange={(v) => setField('pix_key_type', v)}
                  >
                    <SelectTrigger id="pix_key_type">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {PIX_KEY_TYPES.map((k) => (
                        <SelectItem key={k.value} value={k.value}>
                          {k.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pix_key">Chave PIX</Label>
                  <Input
                    id="pix_key"
                    value={form.pix_key ?? ''}
                    onChange={(e) => setField('pix_key', e.target.value)}
                    placeholder="Sua chave PIX"
                  />
                </div>
              </div>
            </div>
          </section>

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={updateProfile.isPending}
              className="text-[hsl(var(--aff-primary-foreground))] hover:opacity-90 shadow-[var(--aff-shadow-soft)] border-0 px-6"
              style={{ background: 'var(--aff-gradient-hero)' }}
            >
              {updateProfile.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Salvar alterações
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
